import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tag, Users, MapPin, Percent, Sparkle } from '@phosphor-icons/react';

const ClientPromotionsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/promotions/my-area')
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], count: 0, area: null, needs_area: true }))
      .finally(() => setLoading(false));
  }, []);

  const items = data?.items || [];
  const area = data?.area;

  return (
    <div className="max-w-5xl mx-auto" data-testid="client-promotions-page">
      <div className="mb-8">
        <h1 className="font-heading font-semibold text-4xl text-foreground mb-2">Promozioni</h1>
        <p className="text-foreground/70">
          {area?.province
            ? <>Prestazioni in promozione nella provincia di <strong>{area.province}</strong>.</>
            : 'Prestazioni in promozione vicino a te.'}
        </p>
      </div>

      {loading ? (
        <Card className="neumorphism rounded-2xl p-8 text-center text-foreground/50">Caricamento…</Card>
      ) : data?.needs_area ? (
        <Card className="neumorphism rounded-2xl p-8 text-center" data-testid="promotions-no-area">
          <MapPin size={40} weight="duotone" className="mx-auto text-primary mb-3" />
          <p className="font-heading font-medium text-lg mb-2">Non conosciamo ancora la tua zona</p>
          <p className="text-sm text-foreground/60 mb-5">
            Crea la tua prima richiesta indicando provincia e comune: qui troverai le promozioni dei
            professionisti della tua provincia.
          </p>
          <Button className="rounded-full" onClick={() => navigate('/richiesta')} data-testid="promotions-create-request-btn">
            Crea una richiesta
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="neumorphism rounded-2xl p-8 text-center" data-testid="promotions-empty">
          <Sparkle size={40} weight="duotone" className="mx-auto text-primary mb-3" />
          <p className="font-heading font-medium text-lg mb-2">Nessuna promozione attiva</p>
          <p className="text-sm text-foreground/60">
            Al momento non ci sono promozioni nella provincia di {area?.province}. Torna a trovarci:
            i professionisti ne pubblicano di nuove ogni mese.
          </p>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Tag size={18} weight="duotone" className="text-primary" />
            <span className="text-sm text-foreground/70">Promozioni disponibili</span>
            <Badge className="bg-primary/10 text-primary border-0">{items.length}</Badge>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((p) => (
              <Card key={p.promotion_id} className="neumorphism rounded-2xl p-5 border-l-4 border-l-accent" data-testid={`client-promo-${p.promotion_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="font-heading font-medium">{p.service_name}</p>
                  <Badge className="bg-amber-100 text-amber-900 border-0 whitespace-nowrap font-semibold">
                    <Percent size={12} className="mr-1" />
                    {p.discount_type === 'percent' ? `-${p.discount_value}%` : `-€${p.discount_value}`}
                  </Badge>
                </div>
                <Link to={`/professional/${p.professional_id}`} className="text-sm text-primary hover:underline" data-testid={`client-promo-pro-${p.promotion_id}`}>
                  {p.professional?.name}
                </Link>
                <p className="text-xs text-foreground/60 mt-0.5">
                  {[p.professional?.profession, p.professional?.comune || p.professional?.province].filter(Boolean).join(' · ')}
                </p>
                <div className="flex items-end justify-between mt-3">
                  <div>
                    {p.original_price != null && (
                      <span className="text-sm text-foreground/50 line-through mr-2">€{p.original_price}</span>
                    )}
                    {p.promo_price != null && (
                      <span className="font-heading font-semibold text-xl text-sky-700">€{p.promo_price}</span>
                    )}
                  </div>
                  <span className="text-xs text-foreground/60 flex items-center gap-1">
                    <Users size={12} /> {p.slots_left} posti
                  </span>
                </div>
                <Button
                  size="sm"
                  className="rounded-full w-full mt-4"
                  onClick={() => navigate(`/richiesta?promo=${p.promotion_id}`)}
                  data-testid={`client-promo-request-${p.promotion_id}`}
                >
                  Richiedi questa prestazione
                </Button>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientPromotionsPage;
